/**
 * User Drilldown Modal — detailed individual productivity stats.
 * Shared by ProductivityAnalytics (Top Performers) and LiveOperations (team table).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import { Loader2, User, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../ui/dialog';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const UserDrilldownModal = ({ open, onClose, userId, userName }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30');
  const [exporting, setExporting] = useState(false);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: fmt, days: period, tz_offset: new Date().getTimezoneOffset() });
      const res = await axios.get(`${API}/api/productivity-analytics/export/user/${userId}?${params}`, { responseType: 'blob' });
      const match = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `PNA_Processor_Report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt === 'pdf' ? 'PDF report' : 'Excel workbook'} downloaded`);
    } catch (e) {
      toast.error('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/productivity-analytics/user-drilldown/${userId}?days=${period}`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching user data:', err);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [userId, period]);

  useEffect(() => {
    if (open && userId) fetchData();
  }, [open, userId, fetchData]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-drilldown-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            {userName || 'User'} - Performance Details
          </DialogTitle>
          <DialogDescription>
            Detailed productivity metrics and trends
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Period Selector + Export */}
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="capitalize">
                {data.user?.role?.replace(/_/g, ' ')}
              </Badge>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50"
                  disabled={exporting} onClick={() => handleExport('pdf')} data-testid="drilldown-export-pdf-btn">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  disabled={exporting} onClick={() => handleExport('xlsx')} data-testid="drilldown-export-xlsx-btn">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                </Button>
                <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                </SelectContent>
                </Select>
              </div>
            </div>

            {/* Target vs Actual */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Today</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold text-gray-900">
                      {data.today_progress?.completed || 0}
                    </span>
                    <span className="text-lg text-gray-400 mb-1">
                      / {data.target?.daily ?? '—'}
                    </span>
                  </div>
                  <Progress
                    value={data.today_progress?.progress_percent || 0}
                    className={`h-2 mt-2 ${
                      (data.today_progress?.progress_percent || 0) >= 100 ? '[&>div]:bg-green-500' :
                      (data.today_progress?.progress_percent || 0) >= 75 ? '[&>div]:bg-blue-500' :
                      '[&>div]:bg-amber-500'
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {data.target?.daily == null
                      ? 'No daily target configured'
                      : data.today_progress?.remaining > 0
                        ? `${data.today_progress.remaining} more to reach target`
                        : '✓ Daily target achieved!'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">This Week</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold text-gray-900">
                      {data.week_progress?.completed || 0}
                    </span>
                    <span className="text-lg text-gray-400 mb-1">
                      / {data.target?.weekly ?? '—'}
                    </span>
                  </div>
                  <Progress
                    value={data.week_progress?.progress_percent || 0}
                    className="h-2 mt-2 [&>div]:bg-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {data.week_progress?.days_remaining || 0} days remaining
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Summary KPI cards (Total/Daily Avg/Avg Time/Days Met Target)
                removed (Jun 2026, user request) — redundant with the
                trend charts and detailed stats below. */}

            {/* Daily Completion Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Daily Completion Trend</CardTitle>
                <CardDescription>Completed files vs daily target</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.daily_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional chart axis tick
                      tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(d) => formatDateOnly(d)}
                      formatter={(value, name) => [value, name === 'completed' ? 'Completed' : 'Target']}
                    />
                    <Bar dataKey="completed" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="target" stroke="#EF4444" strokeDasharray="5 5" dot={false} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No data available
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserDrilldownModal;
